<script setup lang="ts" generic="T">
import Multiselect from "vue-multiselect";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import { tagFamilyColorType } from "~/utils/color/colorMapping";
import { isNoResponseError } from "~/utils/helpers/httpErrors";

const isLoading = ref(false);
const searchQuery = ref("");
const tagSelectRef = ref(null);
const multiselectRef = ref<HTMLElement | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
const offset = ref(0); // to track the offset for lazy loading
const emit = defineEmits(["update:modelValue", "clickAdd", "searchValue", "open"]);

const props = defineProps({
  options: {
    type: [Array<T>],
    default() {
      return [];
    },
  },
  store: {
    type: Object,
    default: null,
  },
  storeObject: {
    type: String,
    default: null,
  },
  modelValue: {
    type: null,
  },
  trackBy: {
    type: String,
    default: "name",
    required: true,
  },
  lazyLoad: {
    type: Boolean,
    default: false,
  },
  add: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "name",
  },
  showNoOptions: {
    type: Boolean,
    default: false,
  },
  showNoResults: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: "Choisir une option",
  },
  groupedOptions: {
    type: [Array<T>],
    default() {
      return [];
    },
  },
  groupValues: {
    type: String,
    default: null,
  },
  groupLabel: {
    type: String,
    default: null,
  },
  groupSelect: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: null,
  },
  error: {
    type: Boolean,
    default: false,
  },
  openFetch: {
    type: Boolean,
    default: false,
  },
  deletedKey: {
    type: String,
    default: "",
  },
  servicesAPI: {
    type: Function,
  },
});

onMounted(async () => {
  if (props.servicesAPI) {
    asyncFind("", true);
    await nextTick();
    scrollContainer.value = multiselectRef.value?.querySelector(".multiselect__content-wrapper") as HTMLElement;
    scrollContainer.value?.addEventListener("scroll", handleScroll);
  }
});

interface QueryParams {
  [key: string]: any;
}
function updateValue(newValue: any) {
  emit("update:modelValue", newValue);
}
async function asyncFind(query: string, init?: boolean) {
  // Imperative fetch — do NOT wrap in useAsyncData (since Nuxt 4 it caches by key and replays
  // the cached result without re-running the handler, and the static "list" key was shared
  // across every instance). servicesAPI (SDK) is fresh on every call.
  if (!init) isLoading.value = true;
  isLoading.value = true;
  const queryParams: QueryParams = {
    limit: 10,
    offset: 0,
    ...{ search: query },
  };

  let api: any;
  try {
    api = await props.servicesAPI?.(queryParams);
  } catch (error) {
    isLoading.value = false;
    // No answer came back: nothing to fix in the app, and the next search asks again.
    if (isNoResponseError(error)) return;
    throw error;
  }

  // `api` can legitimately be undefined here: a `servicesAPI` that skips (e.g. readonly) resolves
  // to nothing. Guard `api` itself — the `?.` after `.data` only protected `.value` onward, so
  // `api.data` still threw when `api` was undefined.
  const data = api?.data?.value?.data?.length ? api.data.value.data : api?.data;
  if (data?.length) {
    props.store[props.storeObject] = [...data];
  }
  offset.value += 10;
  isLoading.value = false;
  if (!init) isLoading.value = false;
}

function getSearchQuery(query: string) {
  searchQuery.value = query;
}
function searchChange(q: string) {
  offset.value = 0;

  if (props.servicesAPI) {
    asyncFind(q, true).then(() => {
      nextTick(() => {
        if (scrollContainer.value) {
          scrollContainer.value.scrollTop = 0; // Reset scroll position
        }
      });
    });
    getSearchQuery(q);
  } else if (props.add) {
    getSearchQuery(q);
  } else {
    return null;
  }
}
function clickAdd() {
  emit("clickAdd");
  // @ts-ignore
  tagSelectRef.value.deactivate();
}

function handleScroll() {
  if (
    scrollContainer.value &&
    scrollContainer.value.scrollTop + scrollContainer.value.clientHeight >= scrollContainer.value.scrollHeight
  ) {
    asyncFind(searchQuery.value); // Load more results when scrolled to bottom
  }
}

function open() {
  if (props.openFetch) {
    emit("open");
  }
}

// Inline rather than a tag_* class: the scoped multiselect styles outrank global classes on these chips.
function familyTagColors(option: Record<string, unknown>) {
  const familyType = option[props.deletedKey] ? undefined : tagFamilyColorType(option[props.color] as string | null);
  const token = (familyType ?? ColorsTypeEnum.GREY).replaceAll("_", "-");
  return { background: `var(--tag-${token}-bg)`, color: `var(--tag-${token}-text)` };
}
</script>
<template>
  <div ref="multiselectRef" class="tags">
    <multiselect
      ref="tagSelectRef"
      :group-values="props.groupValues"
      :group-label="groupLabel"
      :group-select="groupSelect"
      :model-value="props.modelValue"
      :options="props.options"
      :loading="isLoading"
      :track-by="trackBy"
      :label="label"
      :placeholder="placeholder"
      :max-height="200"
      :multiple="true"
      :taggable="false"
      :show-labels="false"
      :show-pointer="false"
      open-direction="bottom"
      :show-no-results="showNoResults"
      :disabled="disabled"
      :show-no-options="showNoOptions"
      :hide-selected="true"
      class="grouped"
      :class="{ error: props.error }"
      @search-change="searchChange"
      @update:modelValue="updateValue"
      @open="open"
    >
      <template #tag="{ option, remove }">
        <div>
          <div class="multiselect__tag grouped-tag" :style="familyTagColors(option)">
            <div class="name flex flex-row gap-2 min-w-0">
              <div class="truncate min-w-0 flex-1" :style="{ color: familyTagColors(option).color }">
                <span name="tag" :option="option" :class="{ 'line-through': option[deletedKey] }">{{
                  option.name
                }}</span>
              </div>
            </div>
            <SvgIcon
              name="close"
              alt=""
              class="dropdown__img__close flex-shrink-0"
              :style="{ color: familyTagColors(option).color }"
              @mousedown.prevent.stop="remove(option)"
            ></SvgIcon>
          </div>
        </div>
      </template>
      <template v-if="props.options.length || searchQuery.length" #caret="{ toggle, isOpen }">
        <span class="multiselect__select">
          <SvgIcon class="w-full h-full" name="arrow_down" @mousedown.prevent.stop="toggle"></SvgIcon>
        </span>
      </template>
      <template v-if="props.add && searchQuery.length" #afterList>
        <div class="dropdown__padding inline-flex gap-[8px] items-center text-primary-blue-color" @click="clickAdd">
          <SvgIcon name="plus"></SvgIcon>
          <span>
            <slot name="add">Ajouter "{{ searchQuery }}"</slot>
          </span>
        </div>
      </template>
      <template #noResult>
        <span>Aucun résultat</span>
      </template>
    </multiselect>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/dropdowns/multiselect";

// Force tags to be placed in a horizontal line
.tags .multiselect__tags {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 4px !important;
  align-items: center !important;
}

.grouped-tag {
  max-width: 250px; // Max width for truncation
  width: fit-content; // Natural width of the content (no fixed min-width)
  overflow: hidden;
  flex-shrink: 0;

  .name {
    max-width: calc(250px - 32px); // Fixed width for consistent truncation (218px)
    overflow: hidden;

    .truncate {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
      display: block; // Force truncation behavior
    }
  }
}
</style>
